import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarIcon, ExternalLink, Headphones, PlaySquare, Youtube } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/SEO";
import {
  LISTED_PLATFORMS,
  PODCAST_NAME,
  PODCAST_TAGLINE,
  PODCAST_YOUTUBE_CHANNEL,
} from "@/config/podcastPlatforms";

// PUBLIC podcast page. Deliberately mirrors the /blog page's structure (gradient hero → max-w-7xl
// body → featured item → grid → sticky sidebar → CTA) so the two content surfaces feel like one site.
//
// Data comes from the `fetch-media-feeds` edge function's `podcast` key, NOT from the
// `podcast_episodes` table: that table's only RLS policy (`audience_insight_read`) is
// authenticated-only, so an anonymous visitor reads zero rows. The edge function is
// anon-callable and already merges the anchor.fm RSS with the YouTube podcast playlist, which
// together are the complete Ep 1-21 thread. See that function's header for why the merge exists.
//
// No <PublicLayout> wrapper here — publicRoutes() is composed inside a layout by every entry.

type PodcastEpisode = {
  id: string;
  episode_number: number | null;
  title: string;
  raw_title: string;
  description: string | null;
  published_at: string | null;
  thumbnail_url: string | null;
  youtube_video_id: string | null;
  youtube_url: string | null;
  /** The episode's page on the audio host — the only per-episode link for Eps 1-6. */
  episode_url: string | null;
  audio_url: string | null;
  sources: Array<"youtube" | "rss">;
};

/**
 * Where an episode card should link.
 *
 * Prefer the episode's OWN page on whichever platform carries it, and only fall back to a
 * show-level landing page when neither feed gave us one. Episodes 1-6 come from the audio RSS
 * alone (YouTube caps its playlist feed at 15 entries), so without episode_url they would all
 * point at the YouTube playlist — a page that does not list them.
 */
const episodeLink = (ep: PodcastEpisode) => {
  if (ep.youtube_url) return { href: ep.youtube_url, label: "Watch on YouTube" };
  if (ep.episode_url) return { href: ep.episode_url, label: "Listen to this episode" };
  const fallback = LISTED_PLATFORMS[0];
  return fallback ? { href: fallback.url, label: `Listen on ${fallback.label}` } : null;
};

type FeedsResponse = {
  podcast?: PodcastEpisode[];
  error?: string;
};

const formatDate = (s: string | null) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const PlatformLinks = ({ size = "default" }: { size?: "default" | "sm" }) => (
  <div className="flex flex-wrap gap-3">
    {LISTED_PLATFORMS.map((p) => (
      <Button
        key={p.key}
        asChild
        size={size}
        className={`${p.className} text-white border-0`}
      >
        <a href={p.url} target="_blank" rel="noopener noreferrer">
          {p.key === "youtube" ? (
            <Youtube className="h-4 w-4 mr-2" />
          ) : (
            <Headphones className="h-4 w-4 mr-2" />
          )}
          {p.label}
        </a>
      </Button>
    ))}
  </div>
);

/** Poster + play affordance shared by the featured and grid cards. */
const EpisodeThumb = ({ ep, className }: { ep: PodcastEpisode; className: string }) =>
  ep.thumbnail_url ? (
    <div className={`${className} overflow-hidden bg-muted relative`}>
      <img
        src={ep.thumbnail_url}
        alt={ep.title}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
      {ep.episode_number != null && (
        <Badge className="absolute top-4 left-4">Episode {ep.episode_number}</Badge>
      )}
    </div>
  ) : (
    <div className={`${className} bg-muted flex items-center justify-center`}>
      <PlaySquare className="h-10 w-10 text-muted-foreground" />
    </div>
  );

const Podcast = () => {
  const { data, isLoading, error } = useQuery<FeedsResponse>({
    queryKey: ["podcast-episodes"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<FeedsResponse>(
        "fetch-media-feeds",
        { method: "GET" },
      );
      if (error) throw error;
      if (!data) throw new Error("No data");
      return data;
    },
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const episodes = data?.podcast ?? [];
  const featured = episodes[0];
  const rest = episodes.slice(1);

  return (
    <div className="py-8">
      <SEO
        title={`${PODCAST_NAME} — The Cape Berkshire Podcast`}
        description={PODCAST_TAGLINE}
        path="/podcast"
      />

      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{PODCAST_NAME}</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto mb-8">
              {PODCAST_TAGLINE}
            </p>
            <div className="flex justify-center">
              <PlatformLinks />
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : error || episodes.length === 0 ? (
          <div className="text-center py-16">
            <Headphones className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-3">Episodes are on their way</h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
              We couldn't load the episode list just now. You can still listen to every episode
              directly on any of the platforms below.
            </p>
            <div className="flex justify-center">
              <PlatformLinks />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main column */}
            <div className="lg:col-span-3">
              {/* Latest episode */}
              {featured && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold mb-6">Latest episode</h2>
                  <Card className="overflow-hidden border-0 shadow-lg">
                    {featured.youtube_video_id ? (
                      <div className="aspect-video bg-muted">
                        <iframe
                          src={`https://www.youtube-nocookie.com/embed/${featured.youtube_video_id}`}
                          title={featured.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          loading="lazy"
                          className="w-full h-full border-0"
                        />
                      </div>
                    ) : (
                      <EpisodeThumb ep={featured} className="aspect-video" />
                    )}
                    <CardHeader>
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        {featured.episode_number != null && (
                          <Badge>Episode {featured.episode_number}</Badge>
                        )}
                        {featured.published_at && (
                          <span className="flex items-center text-sm text-muted-foreground">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {formatDate(featured.published_at)}
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-2xl">{featured.title}</CardTitle>
                      {featured.description && (
                        <CardDescription className="text-base line-clamp-3">
                          {featured.description}
                        </CardDescription>
                      )}
                    </CardHeader>
                    {(featured.audio_url || episodeLink(featured)) && (
                      <CardContent className="space-y-3">
                        {featured.audio_url && (
                          <audio controls preload="none" src={featured.audio_url} className="w-full">
                            Your browser does not support the audio element.
                          </audio>
                        )}
                        {/* Only shown when there is no embedded player above — otherwise the
                            iframe already is the way to watch this episode. */}
                        {!featured.youtube_video_id && (() => {
                          const link = episodeLink(featured);
                          return link ? (
                            <a
                              href={link.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                            >
                              {link.label}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : null;
                        })()}
                      </CardContent>
                    )}
                  </Card>
                </div>
              )}

              {/* All episodes */}
              {rest.length > 0 && (
                <>
                  <h2 className="text-2xl font-bold mb-6">All episodes</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {rest.map((ep) => {
                      const link = episodeLink(ep);
                      const href = link?.href ?? "#";
                      return (
                        <Card
                          key={ep.id}
                          className="group overflow-hidden hover:shadow-lg transition-shadow flex flex-col"
                        >
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            <EpisodeThumb ep={ep} className="aspect-video" />
                          </a>
                          <CardHeader className="flex-1">
                            {ep.published_at && (
                              <span className="flex items-center text-xs text-muted-foreground mb-2">
                                <CalendarIcon className="h-3 w-3 mr-1" />
                                {formatDate(ep.published_at)}
                              </span>
                            )}
                            <CardTitle className="text-lg line-clamp-2">
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-primary transition-colors"
                              >
                                {ep.title}
                              </a>
                            </CardTitle>
                            {ep.description && (
                              <CardDescription className="text-sm line-clamp-3">
                                {ep.description}
                              </CardDescription>
                            )}
                          </CardHeader>
                          <CardContent className="pt-0">
                            {ep.audio_url && (
                              <audio
                                controls
                                preload="none"
                                src={ep.audio_url}
                                className="w-full mb-3"
                              >
                                Your browser does not support the audio element.
                              </audio>
                            )}
                            {link && (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                              >
                                {link.label}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Listen &amp; subscribe</CardTitle>
                    <CardDescription>
                      New episodes land every few weeks. Follow the show wherever you listen.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <PlatformLinks size="sm" />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">About the show</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-3">
                    <p>
                      {PODCAST_NAME} is the Cape Berkshire Asset Management podcast, hosted by Mark
                      Insley and Sam Hallett. Each episode unpacks the macro backdrop, what it means
                      for asset allocation, and how it feeds through to our portfolios.
                    </p>
                    <p>
                      {episodes.length} episode{episodes.length === 1 ? "" : "s"} available.
                    </p>
                    <Button variant="outline" size="sm" asChild className="w-full">
                      <a href={PODCAST_YOUTUBE_CHANNEL} target="_blank" rel="noopener noreferrer">
                        <Youtube className="h-4 w-4 mr-2" />
                        Our YouTube channel
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Want to talk through what this means for your portfolio?
          </h2>
          <p className="text-xl text-primary-foreground/90 max-w-2xl mx-auto mb-8">
            Our investment team is always happy to go deeper than a 20-minute episode allows.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <a href="/contact">Get in touch</a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Podcast;
